import React, { Fragment, useState, useRef } from 'react';
import styled from 'styled-components';
import { useScrollPosition } from '@n8tb1t/use-scroll-position'
import { isMobile } from 'react-device-detect';
import LazyLoad from 'react-lazy-load';

import { H3, Divider, MText, Button, Spacer } from '@lafabrica/core';

const height = window.innerHeight
const panelHeight = Math.max(600, height);

const width = window.innerWidth;


const Wrapper = styled.div`
  height: auto;
  min-height: 100vh;
  width: 100vw;
  ${props => props.backgroundImage &&
    `background-color: ${props.dark ? 'var(--black)' : 'var(--white)'};
    background-image: ${props.backgroundImage};
    background-size: cover;
    background-position: center;
    `
  }
  ${props => props.backgroundColor &&
    `background-color: ${props.backgroundColor};`
  }
  display: flex;
  flex-direction: row;
`;

const Side = styled.div`
  width: 50vw;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LeftSide = styled(Side)`
  height: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const RightSide = styled(Side)`
  min-width: 540px;
  align-items: flex-start;
  height: ${props => `(panelHeight + 180) * ${props.panels}px;`}
  position: relative;
`

const Panel = styled.div`
  padding: 0 20px;
  min-height: 600px;
  max-width: 600px;
  margin: auto;
  padding-bottom: 80px;
  text-align: center;

  @media only screen and (min-width: 769px) {
    height: 90vh;
    padding: 0 40px;
    padding-top: 180px;
    text-align: left;
  }
`

const SubTitle = styled.span`
  font-size: 16px;
  font-weight: bold;

  @media only screen and (min-width: 769px) {
    font-size: 20px;
  }
`

const Center = styled.div`
  justify-content: center;


  @media only screen and (min-width: 769px) {
    justify-content: flex-start;
  }
`

export const FeaturesPanel = ({images, items, textColor, subTitleColor='var(--accent)', backgroundImage=null, backgroundColor=false, dark=false, white=false, desktop=false, fixedPermanently=false, trueRatio=false}) => {
  const [fixed, setFixed] = useState(false);
  const [bottomFixed, setBottomFixed] = useState(false);
  const [image, setImage] = useState(images[0]);
  const [idx, setIdx] = useState(1);
  const wrapperRef = useRef(null);
  const [fixedTop, setFixedTop] = useState(200);
  const objectRef = useRef(null);

  const height = window.innerHeight
  const width = window.innerWidth

  // 1.778 ratio
  let FixedObj = styled.div`
    max-width: 290px;
    width: 22.5vh;
    height: 40vh;
    max-height: 600px;

    ${dark ?
    `background-color: rgb(0, 0, 0);
    box-shadow: rgba(0, 0, 0, 0.85) 0px 24px 32px;` :
    `background-color: rgb(243, 244, 245);
    box-shadow: rgba(0, 0, 0, 0.15) 0px 24px 32px;`
    }

    border-radius: 40px;
    padding: 7vh 2.5vh;
    margin: auto;
    margin-bottom: 80px;

    @media only screen and (min-width: 769px) {
      margin-top: 200px;
      margin-bottom: 0px;
      width: 33.8vh;
      height: 60vh;
      padding: 7vh 2.5vh;
      ${fixed && `position: fixed;bottom: 5vh;`}
      ${bottomFixed  && `position: absolute;bottom: 200px;`}
      ${fixedPermanently && `position: relative; bottom: 20px;`}
    }
  `

  if (desktop){
    // 1.6
    FixedObj = styled.div`
      max-width: 290px;
      width: 75vw;
      ${trueRatio ? 'height: 40.7vw;' : 'height: 46.85vw;'}
      max-height: 600px;

      ${dark ?
      `background-color: rgb(0, 0, 0);
      box-shadow: rgba(0, 0, 0, 0.85) 0px 24px 32px;` :
      `background-color: rgb(243, 244, 245);
      box-shadow: rgba(0, 0, 0, 0.15) 0px 24px 32px;`
      }

      border-radius: 20px;
      padding: 3.5vw 3.5vw;
      margin: auto;
      margin-bottom: 80px;

      @media only screen and (min-width: 769px) {
        max-width: 520px;
        margin-top: 200px;
        margin-bottom: 0px;
        width: 70vh;
        ${trueRatio ? 'height: 38vh;' : 'height: 43.75vh;'}
        padding: 2.5vh 2.5vh;
        ${fixed && `position: fixed;bottom:20vh;`}
        ${bottomFixed && `position: absolute;bottom: 200px;`}
        ${fixedPermanently && `position: relative; bottom: 20px;`}
      }
    `
  }

  const InnerWrapper = styled.div`
    width: 100%;
    height: 100%;
    background-size: contain;
    background-repeat: no-repeat;
    background-cover: cover;
    background-position: center;
  `

  function renderPanel(i){

    if (!items[i]){
      return null
    }
    return (
      <Fragment>
        <H3 style={{color: textColor}}>
          {items[i].title}
        </H3>
        <SubTitle style={{color: subTitleColor}}>{items[i].subTitleA}</SubTitle>
        <Spacer times={0.5} />
        <MText style={{color: textColor}}>
          {items[i].subTextA}
        </MText>
        <Spacer/>
        <SubTitle style={{color: subTitleColor}}>{items[i].subTitleB}</SubTitle>
        <Spacer times={0.5} />
        <MText style={{color: textColor}}>
          {items[i].subTextB}
        </MText>
        {items[i].below &&
          <Center>
            <Spacer />
            {items[i].below}
          </Center>
        }
      </Fragment>
    )
  }

  useScrollPosition(({ prevPos, currPos }) => {
    let gap = desktop ? 0.2 : 0.05
    if (width >= 769){
      const position = wrapperRef.current.getBoundingClientRect();
      const otherPosition = objectRef.current.getBoundingClientRect();
      if ((panelHeight - otherPosition.height) - (panelHeight * gap) > otherPosition.top && (-(panelHeight * 0.9 + 180) * items.length) + otherPosition.height + 200 < (position.top) && !fixed){
        setFixed(true);
        setBottomFixed(false);
      } else if ((panelHeight - otherPosition.height) - (panelHeight * gap) <= (position.top + 200) && fixed){
        setFixed(false);
      } else if ((-(panelHeight * 0.9 + 180) * items.length) + otherPosition.height + 200 >= (position.top) && fixed){
        setFixed(false);
        setBottomFixed(true);
      } else if (position.top < -panelHeight && (-(panelHeight * 0.9 + 180) * items.length) + otherPosition.height + 200 < (position.top) && !fixed){
        setFixed(true);
        setBottomFixed(false);
      }

      if (position.top / -panelHeight < 1 && image !== images[0]){
        setImage(images[0])
      } else if (position.top / -panelHeight >= items.length - 1 && image !== images[items.length - 1]){
        setImage(images[items.length - 1])
      } else if (position.top / -panelHeight > 1){
        getIndex()
      }

      function getIndex(){
        let calc = Math.floor(position.top / -panelHeight)
        if (calc !== 0 && calc !== images.length && image !== images[calc]){
          setImage(images[calc])
        }
      }


    }
  })

  if (width < 769){
    return (
      <Wrapper backgroundImage={backgroundImage} dark={dark} style={{flexDirection: 'column'}}
        backgroundColor={backgroundColor}>
        <Divider height={60} />
        {items.map((item, idx) =>
          <Panel key={idx}>
            <FixedObj>
              <InnerWrapper style={{backgroundImage: `url(${images[idx]})`}} />
            </FixedObj>
            {renderPanel(idx)}
          </Panel>
        )}
      </Wrapper>
    )
  }

  return (
    <Wrapper backgroundImage={backgroundImage} dark={dark} ref={wrapperRef} backgroundColor={backgroundColor}>
      <LeftSide>
       {items.map((item, idx) =>
         <Panel key={idx}>
           {renderPanel(idx)}
         </Panel>
       )}
      </LeftSide>
      <RightSide panels={items.length}>
        <FixedObj style={{marginTop: fixedTop}} ref={objectRef}>
          <InnerWrapper style={{backgroundImage: `url(${image})`}}>
          </InnerWrapper>
        </FixedObj>
      </RightSide>
    </Wrapper>
  );
};
